// Google Analytics tracking utilities
import { ENABLE_ANALYTICS } from '../config/config';

declare global {
    interface Window {
        gtag?: (command: string, ...args: any[]) => void;
        dataLayer?: any[];
    }
}

/**
 * Initialize Google Analytics
 * Only loads GA script if analytics is enabled (production environment)
 */
export const initAnalytics = (): void => {
    if (!ENABLE_ANALYTICS) {
        console.log('Google Analytics disabled (non-production environment)');
        return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        window.dataLayer?.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', 'G-RN4993JBJ9');

    // Load GA script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-RN4993JBJ9';
    document.head.appendChild(script);
    
    console.log('Google Analytics initialized');
};

/**
 * Track a page view
 */
export const trackPageView = (path: string, title?: string): void => {
    if (!ENABLE_ANALYTICS) return;
    
    if (typeof window.gtag !== 'undefined') {
        window.gtag('config', 'G-RN4993JBJ9', {
            page_path: path,
            page_title: title || document.title,
        });
    }
};

/**
 * Track a custom event
 */
export const trackEvent = (
    eventName: string,
    eventParams?: Record<string, any>
): void => {
    if (!ENABLE_ANALYTICS) return;
    
    if (typeof window.gtag !== 'undefined') {
        window.gtag('event', eventName, eventParams);
    }
};

/**
 * Track user login
 */
export const trackLogin = (method: string = 'email'): void => {
    trackEvent('login', { method });
};

/**
 * Track user signup
 */
export const trackSignup = (method: string = 'email'): void => {
    trackEvent('sign_up', { method });
};

/**
 * Track search
 */
export const trackSearch = (searchTerm: string): void => {
    trackEvent('search', { search_term: searchTerm });
};

/**
 * Track exceptions/errors
 */
export const trackException = (
    description: string,
    fatal: boolean = false
): void => {
    if (!ENABLE_ANALYTICS) return;
    
    if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'exception', {
            description,
            fatal,
        });
    }
};

export default {
    initAnalytics,
    trackPageView,
    trackEvent,
    trackLogin,
    trackSignup,
    trackSearch,
    trackException,
};
